import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Check, X, Ban } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHeader, GlassCard, GradButton, EmptyState, Field, fmtMoney } from "@/components/erp";
import { SelectBox } from "@/components/Inputs";
import { HesapSec } from "@/components/HesapSec";
import { useAuth } from "@/context/AuthContext";
import { can, PARA, TAHSIL_DURUM, Rozet } from "@/lib/perms";
import api, { errMsg } from "@/lib/api";

const TahsilatDialog = ({ cariler, turler, onClose, onSaved }) => {
  const { ayar } = useAuth();
  const [f, setF] = useState({ cari_id: "", tur: "nakit", tutar: 0, para_birimi: "TRY", kur: 1, tarih: new Date().toLocaleDateString("sv-SE"), vade_tarihi: "", belge_no: "", banka: "", aciklama: "", hesap_id: "" });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const vadeli = ["cek", "senet"].includes(f.tur);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/tahsilatlar", { ...f, vade_tarihi: f.vade_tarihi || null, hesap_id: vadeli ? null : f.hesap_id || null });
      toast.success(`${data.no} kaydedildi · yeni bakiye ${fmtMoney(data.risk.bakiye)}`);
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="glass max-w-lg" data-testid="tahsilat-dialog">
        <DialogHeader><DialogTitle className="font-head">Yeni tahsilat</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Cari *" className="sm:col-span-2"><SelectBox testid="ths-cari" value={f.cari_id} onChange={(v) => set("cari_id", v)} options={cariler.filter((c) => c.tip !== "tedarikci").map((c) => ({ value: c.id, label: `${c.unvan} · bakiye ${fmtMoney(c.risk?.bakiye)}` }))} /></Field>
          <Field label="Tür"><SelectBox testid="ths-tur" value={f.tur} onChange={(v) => set("tur", v)} options={Object.entries(turler).map(([value, label]) => ({ value, label }))} /></Field>
          <Field label="Tarih"><Input data-testid="ths-tarih" type="date" className="bg-white/5" value={f.tarih} onChange={(e) => set("tarih", e.target.value)} /></Field>
          <Field label="Tutar *"><Input data-testid="ths-tutar" type="number" step="any" className="bg-white/5" value={f.tutar} onChange={(e) => set("tutar", Number(e.target.value))} /></Field>
          <Field label="Para birimi"><SelectBox testid="ths-para" value={f.para_birimi} onChange={(v) => setF((s) => ({ ...s, para_birimi: v, kur: v === "TRY" ? 1 : Number(ayar?.kurlar?.[v] || 0) }))} options={PARA} /></Field>
          {f.para_birimi !== "TRY" && <Field label="Kur"><Input data-testid="ths-kur" type="number" step="any" className="bg-white/5" value={f.kur} onChange={(e) => set("kur", Number(e.target.value))} /></Field>}
          {vadeli && <Field label="Vade tarihi *"><Input data-testid="ths-vade" type="date" className="bg-white/5" value={f.vade_tarihi} onChange={(e) => set("vade_tarihi", e.target.value)} /></Field>}
          {(vadeli || ["havale", "kredi_karti", "banka_diger"].includes(f.tur)) && <>
            <Field label={vadeli ? "Çek / senet no" : "Dekont / işlem no"}><Input data-testid="ths-belge" className="bg-white/5" value={f.belge_no} onChange={(e) => set("belge_no", e.target.value)} /></Field>
            <Field label="Banka"><Input data-testid="ths-banka" className="bg-white/5" value={f.banka} onChange={(e) => set("banka", e.target.value)} /></Field>
          </>}
          {!vadeli && <HesapSec testid="ths-hesap" value={f.hesap_id} onChange={(v) => set("hesap_id", v)} para={f.para_birimi} tur={f.tur} label="Giriş hesabı" />}
          <Field label="Açıklama" className="sm:col-span-2"><Input data-testid="ths-aciklama" className="bg-white/5" value={f.aciklama} onChange={(e) => set("aciklama", e.target.value)} /></Field>
        </div>
        {vadeli && <p className="text-xs text-muted-foreground">Çek/senet bakiyeyi hemen düşürür ancak tahsil edilene kadar portföy riski olarak limit kullanımında kalır.</p>}
        <div className="flex justify-end gap-2"><button onClick={onClose} className="px-5 py-2 rounded-full border border-white/10 text-sm" data-testid="ths-vazgec">Vazgeç</button><GradButton onClick={save} disabled={busy} data-testid="ths-kaydet">Kaydet</GradButton></div>
      </DialogContent>
    </Dialog>
  );
};

const TahsilEtDialog = ({ t, onClose, onSaved }) => {
  const [hesap, setHesap] = useState("");
  const kaydet = async () => {
    try { await api.post(`/tahsilatlar/${t.id}/durum`, { durum: "tahsil_edildi", hesap_id: hesap || null }); toast.success(`${t.no} tahsil edildi`); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="glass max-w-md" data-testid="tahsil-et-dialog">
        <DialogHeader><DialogTitle className="font-head">{t.no} · {fmtMoney(t.tutar, t.para_birimi)} tahsil edildi</DialogTitle></DialogHeader>
        <HesapSec testid="tahsil-et-hesap" value={hesap} onChange={setHesap} para={t.para_birimi} tur={t.tur} label="Paranın girdiği hesap" />
        <div className="flex justify-end gap-2"><button onClick={onClose} className="px-5 py-2 rounded-full border border-white/10 text-sm">Vazgeç</button><GradButton onClick={kaydet} data-testid="tahsil-et-kaydet">Kaydet</GradButton></div>
      </DialogContent>
    </Dialog>
  );
};

export default function Tahsilatlar() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [cariler, setCariler] = useState([]);
  const [turler, setTurler] = useState({});
  const [yeni, setYeni] = useState(false);
  const [tahsil, setTahsil] = useState(null);
  const yonet = can(user, "tahsilat:manage");
  const load = useCallback(async () => {
    const [t, c, tr] = await Promise.all([api.get("/tahsilatlar"), api.get("/cariler"), api.get("/tahsilat-turleri")]);
    setList(t.data); setCariler(c.data); setTurler(tr.data);
  }, []);
  useEffect(() => { load(); }, [load]);
  const durum = async (id, d) => {
    try { await api.post(`/tahsilatlar/${id}/durum`, { durum: d }); toast.success("Güncellendi"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const portfoy = list.filter((t) => t.durum === "portfoyde").reduce((s, t) => s + t.tutar_try, 0);

  return (
    <div data-testid="tahsilatlar-page">
      <PageHeader eyebrow="Finans" title="Tahsilatlar" desc={`Her tahsilat cari bakiyesini ve risk kullanımını anında günceller. Portföydeki çek/senet: ${fmtMoney(portfoy)}`}>
        {can(user, "tahsilat:write") && <GradButton data-testid="tahsilat-ekle" onClick={() => setYeni(true)}><Plus className="w-4 h-4" />Yeni tahsilat</GradButton>}
      </PageHeader>
      <GlassCard className="overflow-x-auto">
        {list.length === 0 ? <EmptyState text="Henüz tahsilat yok." testid="tahsilat-bos" /> : (
          <table className="w-full text-sm" data-testid="tahsilat-tablo">
            <thead><tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground border-b border-white/5">
              <th className="p-4">No</th><th className="p-4">Tarih</th><th className="p-4">Cari</th><th className="p-4">Tür</th><th className="p-4 text-right">Tutar</th><th className="p-4">Durum</th><th className="p-4" />
            </tr></thead>
            <tbody>{list.map((t) => (
              <tr key={t.id} className="border-b border-white/5" data-testid={`tahsilat-satir-${t.no}`}>
                <td className="p-4 font-mono text-xs">{t.no}</td><td className="p-4 text-xs">{t.tarih}</td><td className="p-4">{t.cari_unvan}</td>
                <td className="p-4 text-xs">{turler[t.tur]}{t.hesap_ad ? <div className="text-muted-foreground">{t.hesap_ad}</div> : null}{t.vade_tarihi ? <div className="text-muted-foreground">vade {t.vade_tarihi}{t.belge_no ? ` · ${t.belge_no}` : ""}{t.ciro_tedarikci ? ` · ciro: ${t.ciro_tedarikci}` : ""}</div> : null}</td>
                <td className="p-4 text-right font-mono">{fmtMoney(t.tutar, t.para_birimi)}{t.para_birimi !== "TRY" && <div className="text-xs text-muted-foreground">{fmtMoney(t.tutar_try)}</div>}</td>
                <td className="p-4"><Rozet map={TAHSIL_DURUM} d={t.durum} testid={`tahsilat-durum-${t.no}`} /></td>
                <td className="p-4 text-right whitespace-nowrap">{yonet && <>
                  {t.durum === "portfoyde" && <button title="Tahsil edildi" data-testid={`tahsilat-tahsil-${t.no}`} onClick={() => setTahsil(t)} className="p-2 rounded-lg text-muted-foreground hover:text-emerald-300"><Check className="w-4 h-4" /></button>}
                  {t.durum === "portfoyde" && <button title="Karşılıksız" data-testid={`tahsilat-karsiliksiz-${t.no}`} onClick={() => durum(t.id, "karsiliksiz")} className="p-2 rounded-lg text-muted-foreground hover:text-red-300"><X className="w-4 h-4" /></button>}
                  {["portfoyde", "tahsil_edildi"].includes(t.durum) && <button title="İptal" data-testid={`tahsilat-iptal-${t.no}`} onClick={() => durum(t.id, "iptal")} className="p-2 rounded-lg text-muted-foreground hover:text-red-300"><Ban className="w-4 h-4" /></button>}
                </>}</td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </GlassCard>
      {yeni && <TahsilatDialog cariler={cariler} turler={turler} onClose={() => setYeni(false)} onSaved={() => { setYeni(false); load(); }} />}
      {tahsil && <TahsilEtDialog t={tahsil} onClose={() => setTahsil(null)} onSaved={() => { setTahsil(null); load(); }} />}
    </div>
  );
}
